import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dtpApi } from '../lib/dtpApi';
import { useDtpAuthStore } from '../store/useDtpAuthStore';

/**
 * Spends a `?pi=` handoff code from the clinic portal for a patient session.
 *
 * A clinic's storefront may be on its own domain, so the portal cannot write a
 * session for it and instead sends a single-use code. The code is burned
 * server-side on the first exchange, so this runs once per mount and strips the
 * parameter either way — a spent code left in the address bar is useless, but
 * it should not survive into history or a shared screenshot regardless.
 *
 * The resulting session is marked as impersonated, which the layout surfaces
 * and the backend enforces: it cannot spend the patient's money.
 */
export function usePatientImpersonationHandoff() {
  const [searchParams, setSearchParams] = useSearchParams();
  const setAuth = useDtpAuthStore((s) => s.setAuth);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    const code = searchParams.get('pi');
    if (!code) return;
    ran.current = true;

    setOpening(true);
    (async () => {
      try {
        const res = await dtpApi.exchangeImpersonationCode(code);
        if (res.success && res.data) {
          setAuth(res.data.token, res.data.patient, res.data.physicianId, true);
        } else {
          setError(res.error || 'This portal link has expired or has already been used.');
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || 'This portal link has expired or has already been used.');
      } finally {
        const cleaned = new URLSearchParams(searchParams);
        cleaned.delete('pi');
        setSearchParams(cleaned, { replace: true });
        setOpening(false);
      }
    })();
  }, [searchParams, setAuth, setSearchParams]);

  return { opening, error };
}
